// Configures the posthog-js singleton at app boot. Other modules can then
// `import posthog from "posthog-js"` and call identify / capture directly.
import posthog from "posthog-js"
import { posthog as config } from "@/common/config"
import { sanitizeIdentityBeforeSend } from "@/init/posthogSanitize"
import {
  maskIdentityNetworkRequest,
  shouldDisableSessionRecordingAtInit,
} from "@/init/posthogSessionRecording"

// Whether posthog.init actually runs below. Exported so
// posthogSessionRecording.ts's router-driven start/stopSessionRecording
// guard (installed from main.ts) can skip itself entirely in local dev / when
// no API key is configured, rather than relying on posthog-js's own pre-init
// no-op behavior.
export const posthogEnabled = !import.meta.env.DEV && !!config.api_key

// Skip PostHog entirely in local development. `capture`/`identify` calls
// elsewhere are safe no-ops when the library was never initialized.
if (posthogEnabled && config.api_key) {
  posthog.init(config.api_key, {
    api_host: "/relay-ph",
    ui_host: "https://us.posthog.com",
    // Keeps the single-use Stytch credentials that /identity/* routes carry
    // in their URL (discovery token, OAuth authorize params, ...) out of
    // captured analytics EVENTS, without touching UTM params or anything else
    // reported for the rest of the app. See posthogSanitize.ts for the full
    // rationale, including why this is `before_send` and not the (deprecated,
    // in this posthog-js version) `sanitize_properties` option. This does NOT
    // cover session replay — see the three layers below and
    // posthogSessionRecording.ts.
    before_send: sanitizeIdentityBeforeSend,

    // --- Session replay: a human confirmed on 2026-09-22 that it IS enabled
    // for this project. Three layers, in order of how much they can be
    // trusted — see posthogSessionRecording.ts for the full rationale.

    // Layer 1 (init-time half; the router-driven other half is installed
    // from main.ts). The guarantee: stops the WHOLE replay pipeline (DOM
    // snapshots, mutations, network capture) on /identity/*, not just a URL
    // field. Stytch redirects the browser DIRECTLY to /identity/callback, so
    // landing there on a fresh page load — before vue-router even exists —
    // is the common case this has to cover.
    disable_session_recording: shouldDisableSessionRecordingAtInit(window.location.pathname),

    // Layer 2: best-effort, not a guarantee. Its documented behaviour
    // (@posthog/types JSDoc) is masking personal-data-shaped properties
    // "from the current URL" — which reads like ordinary event PROPERTIES.
    // I could not establish from the installed typings whether it also
    // reaches replay's own URL capture. Layer 1 above is the guarantee for
    // identity pages; this is a second layer whose reach is not fully known.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [
      "token",
      "stytch_token_type",
      "origin_assertion",
      "code",
      "state",
    ],

    // Layer 3: redacts the one network request whose body could carry a LIVE
    // Stytch credential (not just the single-use discovery token — see
    // posthogSessionRecording.ts) regardless of whether the layers above
    // hold. Inert under this repo's current session_recording config, added
    // as low-cost insurance anyway; full reasoning in
    // posthogSessionRecording.ts.
    session_recording: {
      maskCapturedNetworkRequestFn: maskIdentityNetworkRequest,
    },
  })

  // Super properties on every event, $pageview and autocapture included:
  // which product sent it and from which environment (common/config.ts).
  posthog.register({ surface: config.surface, environment: config.environment })
}
